import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useCreatePlanMutation, useUpdatePlanMutation } from '../../../store/api/superAdminApi';
import Modal from '../../../components/common/Modal';
import toast from 'react-hot-toast';

const CreateEditPlanModal = ({ plan, isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    originalPrice: '',
    discountPercentage: 0,
    price: '',
    currency: 'USD',
    billingType: 'monthly',
    trialPeriod: 0,
    status: 'active',
    features: [''],
    countries: [],
    limits: {
      maxEmployees: '',
      maxBranches: '',
      storageLimit: ''
    }
  });

  const [createPlan, { isLoading: isCreating }] = useCreatePlanMutation();
  const [updatePlan, { isLoading: isUpdating }] = useUpdatePlanMutation();

  const isEditing = !!plan;
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan.name || '',
        code: plan.code || '',
        description: plan.description || '',
        originalPrice: plan.originalPrice?.toString() || '',
        discountPercentage: plan.discountPercentage || 0,
        price: plan.price?.monthly?.toString() || '',
        currency: plan.currency || 'USD',
        billingType: plan.billingType || 'monthly',
        trialPeriod: plan.trialDays || 0,
        status: plan.isActive ? 'active' : 'inactive',
        features: plan.features?.modules || [''],
        countries: plan.countries || [],
        limits: {
          maxEmployees: plan.features?.maxEmployees?.toString() || '',
          maxBranches: plan.features?.maxBranches?.toString() || '',
          storageLimit: plan.features?.storage?.toString() || ''
        }
      });
    } else {
      // Reset form for new plan
      setFormData({
        name: '',
        code: '',
        description: '',
        originalPrice: '',
        discountPercentage: 0,
        price: '',
        currency: 'USD',
        billingType: 'monthly',
        trialPeriod: 0,
        status: 'active',
        features: [''],
        countries: [],
        limits: {
          maxEmployees: '',
          maxBranches: '',
          storageLimit: ''
        }
      });
    }
  }, [plan]);

  const calculateDiscountedPrice = (originalPrice, discountPercentage) => {
    if (!originalPrice || !discountPercentage) return originalPrice || 0;
    const discount = (originalPrice * discountPercentage) / 100;
    return originalPrice - discount;
  };

  const getCurrencySymbol = (curr) => {
    const symbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'CAD': 'C$',
      'AUD': 'A$',
      'INR': '₹',
      'SGD': 'S$',
      'AED': 'د.إ',
      'BRL': 'R$',
      'MXN': '$',
      'ZAR': 'R',
      'NGN': '₦',
      'KES': 'KSh',
      'EGP': '£'
    };
    return symbols[curr] || '$';
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    
    if (name.startsWith('limits.')) {
      const limitField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        limits: {
          ...prev.limits,
          [limitField]: value
        }
      }));
    } else {
      const newFormData = {
        ...formData,
        [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value
      };

      // Auto-calculate price when original price or discount percentage changes
      if (name === 'originalPrice' || name === 'discountPercentage') {
        const originalPrice = name === 'originalPrice' ? parseFloat(value) || 0 : parseFloat(formData.originalPrice) || 0;
        const discountPercentage = name === 'discountPercentage' ? parseFloat(value) || 0 : parseFloat(formData.discountPercentage) || 0;
        const calculatedPrice = calculateDiscountedPrice(originalPrice, discountPercentage);
        newFormData.price = calculatedPrice.toString();
      }

      setFormData(newFormData);
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    if (formData.features.length > 1) {
      const newFeatures = formData.features.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        features: newFeatures
      }));
    }
  };

  const handleCountryChange = (countryCode) => {
    setFormData(prev => ({
      ...prev,
      countries: prev.countries.includes(countryCode)
        ? prev.countries.filter(c => c !== countryCode)
        : [...prev.countries, countryCode]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.code || !formData.originalPrice) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Filter out empty features
    const cleanFeatures = formData.features.filter(feature => feature.trim() !== '');
    const originalPrice = parseFloat(formData.originalPrice);
    const discountPercentage = parseFloat(formData.discountPercentage) || 0;
    const monthlyPrice = calculateDiscountedPrice(originalPrice, discountPercentage);
    const yearlyPrice = monthlyPrice * 12; // Simply 12 months of the discounted monthly price

    const planData = {
      name: formData.name,
      code: formData.code.toUpperCase(),
      description: formData.description,
      originalPrice: originalPrice,
      discountPercentage: discountPercentage,
      price: {
        monthly: monthlyPrice,
        yearly: yearlyPrice
      },
      currency: formData.currency,
      features: {
        maxEmployees: formData.limits.maxEmployees === '' ? 100 : parseInt(formData.limits.maxEmployees),
        maxBranches: formData.limits.maxBranches === '' ? 5 : parseInt(formData.limits.maxBranches),
        storage: formData.limits.storageLimit === '' ? 10 : parseInt(formData.limits.storageLimit),
        modules: cleanFeatures,
        support: 'basic',
        customizations: false
      },
      isActive: formData.status === 'active',
      trialDays: parseInt(formData.trialPeriod) || 14,
      countries: formData.countries.length > 0 ? formData.countries : ['US', 'IN', 'CA', 'AU', 'GB'], // Default countries if none selected
      sortOrder: 0
    };

    try {
      if (isEditing) {
        await updatePlan({
          id: plan._id,
          ...planData
        }).unwrap();
        toast.success('Plan updated successfully');
      } else {
        await createPlan(planData).unwrap();
        toast.success('Plan created successfully');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.data?.message || `Failed to ${isEditing ? 'update' : 'create'} plan`);
    }
  };

  const currencyOptions = [
    { code: 'USD', name: 'US Dollar' },
    { code: 'EUR', name: 'Euro' },
    { code: 'GBP', name: 'British Pound' },
    { code: 'INR', name: 'Indian Rupee' },
    { code: 'CAD', name: 'Canadian Dollar' },
    { code: 'AUD', name: 'Australian Dollar' },
    { code: 'SGD', name: 'Singapore Dollar' },
    { code: 'AED', name: 'UAE Dirham' }
  ];

  const countryOptions = [
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'IN', name: 'India' },
    { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'JP', name: 'Japan' },
    { code: 'SG', name: 'Singapore' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'BR', name: 'Brazil' },
    { code: 'MX', name: 'Mexico' },
    { code: 'ES', name: 'Spain' },
    { code: 'IT', name: 'Italy' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' },
    { code: 'DK', name: 'Denmark' },
    { code: 'FI', name: 'Finland' },
    { code: 'BE', name: 'Belgium' },
    { code: 'AT', name: 'Austria' },
    { code: 'IE', name: 'Ireland' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'KR', name: 'South Korea' },
    { code: 'TW', name: 'Taiwan' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'TH', name: 'Thailand' },
    { code: 'PH', name: 'Philippines' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'VN', name: 'Vietnam' },
    { code: 'BD', name: 'Bangladesh' },
    { code: 'PK', name: 'Pakistan' },
    { code: 'LK', name: 'Sri Lanka' },
    { code: 'EG', name: 'Egypt' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'TR', name: 'Turkey' },
    { code: 'IL', name: 'Israel' },
    { code: 'RU', name: 'Russia' },
    { code: 'PL', name: 'Poland' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'HU', name: 'Hungary' },
    { code: 'GR', name: 'Greece' },
    { code: 'PT', name: 'Portugal' },
    { code: 'RO', name: 'Romania' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'HR', name: 'Croatia' },
    { code: 'RS', name: 'Serbia' },
    { code: 'UA', name: 'Ukraine' },
    { code: 'CL', name: 'Chile' },
    { code: 'AR', name: 'Argentina' },
    { code: 'CO', name: 'Colombia' },
    { code: 'PE', name: 'Peru' },
    { code: 'EC', name: 'Ecuador' },
    { code: 'UY', name: 'Uruguay' },
    { code: 'PY', name: 'Paraguay' },
    { code: 'BO', name: 'Bolivia' },
    { code: 'VE', name: 'Venezuela' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'PA', name: 'Panama' },
    { code: 'GT', name: 'Guatemala' },
    { code: 'NI', name: 'Nicaragua' },
    { code: 'HN', name: 'Honduras' },
    { code: 'SV', name: 'El Salvador' },
    { code: 'DO', name: 'Dominican Republic' },
    { code: 'JM', name: 'Jamaica' },
    { code: 'TT', name: 'Trinidad and Tobago' },
    { code: 'BB', name: 'Barbados' },
    { code: 'LC', name: 'Saint Lucia' },
    { code: 'GD', name: 'Grenada' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines' },
    { code: 'AG', name: 'Antigua and Barbuda' },
    { code: 'DM', name: 'Dominica' },
    { code: 'KN', name: 'Saint Kitts and Nevis' },
    { code: 'BS', name: 'Bahamas' },
    { code: 'BZ', name: 'Belize' },
    { code: 'GY', name: 'Guyana' },
    { code: 'SR', name: 'Suriname' },
    { code: 'FK', name: 'Falkland Islands' }
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size="xl">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Plan' : 'Create New Plan'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plan Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Professional Plan"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plan Code *
                </label>
                <input
                  type="text"
                  name="code"
                  required
                  value={formData.code}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., PROFESSIONAL"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Brief description of the plan..."
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Original Price *
                </label>
                <input
                  type="number"
                  name="originalPrice"
                  required
                  min="0"
                  step="0.01"
                  value={formData.originalPrice}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="1999.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount %
                </label>
                <input
                  type="number"
                  name="discountPercentage"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.discountPercentage}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Final Monthly Price
                </label>
                <input
                  type="number"
                  name="price"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                  placeholder="999.00"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-calculated</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency
                </label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {currencyOptions.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Trial Period (days)
                </label>
                <input
                  type="number"
                  name="trialPeriod"
                  min="0"
                  value={formData.trialPeriod}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="14"
                />
              </div>
            </div>

            {/* Pricing Summary */}
            {formData.originalPrice && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Pricing Summary</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700">Original Price:</span>
                    <span className="font-semibold text-blue-900 ml-2">
                      {getCurrencySymbol(formData.currency)}{parseFloat(formData.originalPrice || 0).toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-blue-700">Discount ({formData.discountPercentage}%):</span>
                    <span className="font-semibold text-green-600 ml-2">
                      -{getCurrencySymbol(formData.currency)}{((parseFloat(formData.originalPrice || 0) * parseFloat(formData.discountPercentage || 0)) / 100).toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-blue-700">Final Monthly Price:</span>
                    <span className="font-semibold text-blue-900 ml-2">
                      {getCurrencySymbol(formData.currency)}{parseFloat(formData.price || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Country Availability */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Country Availability</h3>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Select the countries where this plan should be available. If none are selected, the plan will be available in default countries (US, India, Canada, Australia, UK).
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-60 overflow-y-auto border rounded-lg p-4 bg-gray-50">
                {countryOptions.map((country) => (
                  <label key={country.code} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.countries.includes(country.code)}
                      onChange={() => handleCountryChange(country.code)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{country.name}</span>
                  </label>
                ))}
              </div>
              
              {formData.countries.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Selected Countries ({formData.countries.length}):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {formData.countries.map((countryCode) => {
                      const country = countryOptions.find(c => c.code === countryCode);
                      return (
                        <span
                          key={countryCode}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {country?.name || countryCode}
                          <button
                            type="button"
                            onClick={() => handleCountryChange(countryCode)}
                            className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600"
                          >
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Limits */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Limits</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Employees
                </label>
                <input
                  type="number"
                  name="limits.maxEmployees"
                  min="1"
                  value={formData.limits.maxEmployees}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Leave empty for unlimited"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for unlimited</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Branches
                </label>
                <input
                  type="number"
                  name="limits.maxBranches"
                  min="1"
                  value={formData.limits.maxBranches}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Leave empty for unlimited"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for unlimited</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Storage Limit (GB)
                </label>
                <input
                  type="number"
                  name="limits.storageLimit"
                  min="1"
                  value={formData.limits.storageLimit}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Leave empty for unlimited"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for unlimited</p>
              </div>
            </div>
          </div>

          {/* Features */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Features</h3>
            <div className="space-y-3">
              {formData.features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter feature description..."
                  />
                  {formData.features.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addFeature}
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
              >
                <PlusIcon className="w-5 h-5" />
                <span>Add Feature</span>
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 
                (isEditing ? 'Updating...' : 'Creating...') : 
                (isEditing ? 'Update Plan' : 'Create Plan')
              }
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CreateEditPlanModal;